import React from "react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Loader2 } from "lucide-react";
import { Column } from "@/components/Datatable/types";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { formatCurrency } from "@/utils/formatCurrency";
import { BillingInvoice } from "../api/getBillingInvoices/types";
import BillingInvoiceStatusBadge from "../components/BillingInvoiceStatusBadge/BillingInvoiceStatusBadge";
import { BillingSortAccessors } from "../hooks/useBillingClientSorting";
import {
  formatBillingDate,
  formatBillingSubscriptionName,
} from "../utils/billingFormatters";

const STATUS_ORDER: Record<BillingInvoice["status"], number> = {
  paid: 1,
  open: 2,
  past_due: 3,
};

export const INVOICE_SORT_ACCESSORS: BillingSortAccessors<BillingInvoice> = {
  number: (invoice) => invoice.number,
  subscription: (invoice) =>
    invoice.subscription &&
    formatBillingSubscriptionName(
      invoice.subscription.productName,
      invoice.subscription.trade,
    ),
  invoiceDate: (invoice) => invoice.invoiceDate,
  totalAmount: (invoice) => Number(invoice.totalAmount),
  balanceAmount: (invoice) => Number(invoice.balanceAmount),
  status: (invoice) => STATUS_ORDER[invoice.status],
};

export function invoiceNumberColumn(
  onViewInvoice: (id: string) => void,
): Column<BillingInvoice> {
  return {
    header: "Invoice",
    accessorKey: "number",
    enableSorting: true,
    cell: ({ row }) => (
      <button
        className="text-primary underline-offset-2 hover:underline"
        onClick={() => onViewInvoice(row.original.id)}
        type="button"
      >
        {row.original.number}
      </button>
    ),
  };
}

export const invoiceDateColumn: Column<BillingInvoice> = {
  header: "Invoice date",
  accessorKey: "invoiceDate",
  enableSorting: true,
  cell: ({ row }) => formatBillingDate(row.original.invoiceDate),
};

export const invoiceAmountColumn: Column<BillingInvoice> = {
  header: "Amount",
  accessorKey: "totalAmount",
  enableSorting: true,
  cell: ({ row }) => formatCurrency(row.original.totalAmount),
};

export const invoiceBalanceColumn: Column<BillingInvoice> = {
  header: "Balance due",
  accessorKey: "balanceAmount",
  enableSorting: true,
  cell: ({ row }) => formatCurrency(row.original.balanceAmount),
};

export const invoiceStatusColumn: Column<BillingInvoice> = {
  header: "Status",
  accessorKey: "status",
  enableSorting: true,
  cell: ({ row }) => <BillingInvoiceStatusBadge status={row.original.status} />,
};

interface InvoiceActions {
  onViewInvoice: (id: string) => void;
  onDownloadPdf: (id: string) => void;
  downloadingInvoiceId: string | null;
}

export function invoiceActionsColumn({
  onViewInvoice,
  onDownloadPdf,
  downloadingInvoiceId,
}: InvoiceActions): Column<BillingInvoice> {
  return {
    id: "actions",
    header: "",
    cell: ({ row }) => (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-8 w-8 p-0 transition-colors hover:bg-gray-200 dark:hover:bg-slate-700"
            variant="ghost"
          >
            <span className="sr-only">Open menu</span>
            <EllipsisVerticalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-[160px] bg-white p-2 dark:bg-slate-800"
        >
          <DropdownMenuItem
            className="flex cursor-pointer items-center px-3 py-2 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
            onSelect={() => onViewInvoice(row.original.id)}
          >
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
            disabled={downloadingInvoiceId !== null}
            onSelect={(event) => {
              // Keeps the menu open so the spinner stays visible.
              event.preventDefault();
              onDownloadPdf(row.original.id);
            }}
          >
            Download PDF
            {downloadingInvoiceId === row.original.id && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-500 dark:text-gray-400" />
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  };
}
